/* eslint-disable no-restricted-syntax */
import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssociationType } from '../../types';
import {
  closestCorners,
  CollisionDetection,
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, pointerWithin, rectIntersection, useDroppable, useSensor, useSensors
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { DroppableList } from './components/DroppableList';
import { AssociationDroppableItem } from './components/AssociationDroppableItem';
import { orderAssociationsAlphabetically } from '../../utils';
import { TemplateFileData } from 'graphql/documents/mutations';

export const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(2) },
  title: {
    fontWeight: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    paddingBottom: theme.spacing(2)
  },
  content: {
    padding: theme.spacing(2)
  },
  subtitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    paddingBottom: "0.3rem"
  },
  titleList: {
    fontWeight: 500,
    fontSize: "0.9rem",
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    textAlign: "center",
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
  },
  dropzoneContainer: {
    borderWidth: 2,
    borderRadius: 2,
    borderColor: 'lightgray',
    borderStyle: 'dashed',
    padding: theme.spacing(5),
    width: '90%'
  },
  fileContainer: {
    borderRadius: '15px',
    background: '#ececec',
    width: 'auto',
    paddingRight: theme.spacing(2)
  },
  removeFileContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#646b6e',
  },
  actionButtons: { padding: theme.spacing(2) },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

type EditBundleContentModalProps = {
	isOpen: boolean,
	onClose: () => void,
	isSavingLoading: boolean,
	parties: AssociationType[],
	currentExpectedByPartiesList: AssociationType[],
	onSave: (data: TemplateFileData[]) => void
}

const allPartiesContainerId = "allParties";
const expectedFromContainerId = "expectedFrom";

export const EditExpectedFromModal: React.FC<EditBundleContentModalProps> = ({
  isOpen,
  onClose,
  isSavingLoading,
  parties,
  currentExpectedByPartiesList,
  onSave
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [allParties, setAllParties] = useState<AssociationType[]>(
    orderAssociationsAlphabetically(
      parties.filter(({ _id }) => !currentExpectedByPartiesList.some(({ _id: expectedPartyId }) => expectedPartyId === _id))
    )
  );
  const [expectedFrom, setExpectedFrom] = useState<AssociationType[]>(orderAssociationsAlphabetically(currentExpectedByPartiesList));
  const [activePartyId, setActivePartyId] = useState<string | null>("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetectionStrategy: CollisionDetection = (args) => {
    const pointerIntersections = pointerWithin(args);

    if (pointerIntersections.length > 0) {
      const overContainer = pointerIntersections.find(
        ({ id }) => [allPartiesContainerId, expectedFromContainerId].includes(id as string)
      );

      if (overContainer) {
        return [overContainer];
      }
    }

    return closestCorners(args);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActivePartyId(active.id as string);
  };

  const activeParty: AssociationType | null = activePartyId
    ? allParties.concat(expectedFrom).find(({ _id }) => _id === activePartyId)!
    : null;

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActivePartyId(null);

    const findItemContainer = (id: string) => {
      if (allParties.find(item => item._id === id)) return allPartiesContainerId;
      if (expectedFrom.find(item => item._id === id)) return expectedFromContainerId;
      return null;
    };

    if (!over) return;

    const activeContainer = findItemContainer(active.id as string);
    const overContainer = [allPartiesContainerId, expectedFromContainerId].includes(over.id as string)
      ? over.id as string
      : findItemContainer(over.id as string);

    if (!activeContainer || !overContainer) return;

    if (activeContainer !== overContainer) {
      const activeItem = activeContainer === expectedFromContainerId
        ? expectedFrom.find(item => item._id === active.id)
        : allParties.find(item => item._id === active.id);
      if (!activeItem) return;

      if (overContainer === expectedFromContainerId) {
        setExpectedFrom(prevBundle => orderAssociationsAlphabetically([activeItem, ...prevBundle]));
        setAllParties(prevAllDoc => prevAllDoc.filter(({ _id }) => _id !== activeItem?._id));
      } else {
        setExpectedFrom(prevBundle => prevBundle.filter(({ _id }) => _id !== activeItem._id));
        setAllParties(prevAllDoc => orderAssociationsAlphabetically([...prevAllDoc, activeItem]));
      }
    }
  }, [expectedFrom, allParties]);

  const handleOnSave = () => {
    onSave(expectedFrom.map(({ _id }) => ({
      _id,
      kind: "party"
    })));
  };

  return (
    <Dialog open={isOpen} keepMounted={false} maxWidth="md" fullWidth>
      <DialogContent style={{ padding: 0, maxWidth: "100%", overflow: "hidden" }}>
        <Box>
          <Typography variant="h5" className={classes.title}>
            {t('documents:uploads')}
          </Typography>

          <Box className={classes.content}>

            <Box style={{ display: "flex", gap: "1rem", width: "100%" }}>
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <DroppableList associations={allParties} title={t('documents:parties')} droppableContainerId={allPartiesContainerId} />
                <DroppableList associations={expectedFrom} title={t('documents:expectedFrom')} droppableContainerId={expectedFromContainerId} />
                <DragOverlay adjustScale={false} style={{ maxWidth: "500px", maxHeight: "100px" }}>
                  {activeParty ? (
                    <AssociationDroppableItem association={activeParty} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton text={t('common:close')} onClick={onClose} disabled={isSavingLoading} />
        <ContainedButton
          text={t('common:save')}
          isLoading={isSavingLoading}
          onClick={handleOnSave}
          disabled={isSavingLoading}
        />
      </DialogActions>
    </Dialog>
  );
};
